import React from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

type IconName = keyof typeof icons;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: IconName;
  rightIcon?: IconName;
  fullWidth?: boolean;
  asChild?: boolean;
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500 dark:bg-primary-500 dark:hover:bg-primary-600',
  secondary: 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200 focus-visible:ring-neutral-400 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700',
  outline: 'border border-neutral-300 bg-transparent text-neutral-800 hover:bg-neutral-100 focus-visible:ring-neutral-400 dark:border-neutral-600 dark:text-neutral-100 dark:hover:bg-neutral-800',
  ghost: 'bg-transparent text-neutral-700 hover:bg-neutral-100 focus-visible:ring-neutral-400 dark:text-neutral-200 dark:hover:bg-neutral-800',
  danger: 'bg-emergency-600 text-white hover:bg-emergency-700 focus-visible:ring-emergency-500 dark:bg-emergency-500 dark:hover:bg-emergency-600',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 py-1.5 text-xs leading-4',
  md: 'h-9 px-4 py-2 text-sm leading-5',
  lg: 'h-10 px-5 py-2.5 text-base leading-6',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      asChild = false,
      className,
      disabled,
      type,
      ...props
    },
    ref
  ) => {
    const renderIcon = (iconName?: IconName) => {
      if (!iconName) return null;
      const Icon = icons[iconName];
      return Icon ? <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> : null;
    };

    const Comp = asChild ? React.Fragment : 'button';

    return (
      <Comp
        ref={asChild ? undefined : ref}
        {...(asChild ? {} : { type: type ?? 'button' })}
        className={cn(
          'inline-flex min-w-0 items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-neutral-900',
          !asChild && variantClasses[variant],
          !asChild && sizeClasses[size],
          fullWidth && 'w-full',
          className
        )}
        {...(asChild ? {} : { disabled: disabled || loading })}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <icons.Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          renderIcon(leftIcon)
        )}
        <span className="min-w-0 truncate">{children}</span>
        {rightIcon && !loading && renderIcon(rightIcon)}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
